"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Journey = exports.journeyAttributesWithComposedAttributes = exports.journeyAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const VisitedPlace_1 = require("./VisitedPlace");
const Trip_1 = require("./Trip");
const TripChain_1 = require("./TripChain");
const StartEndable_1 = require("./StartEndable");
const AnswerStatus_1 = require("./attributeTypes/AnswerStatus");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.journeyAttributes = [
    ...StartEndable_1.startEndDateAndTimesAttributes,
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    '_sequence',
    'name',
    'type',
    'noSchoolTripReason',
    'noSchoolTripReasonSpecify',
    'noWorkTripReason',
    'noWorkTripReasonSpecify',
    'didTrips',
    '_skipTripDiary',
    'previousWeekRemoteWorkDays',
    'previousWeekTravelToWorkDays',
    'preData'
];
exports.journeyAttributesWithComposedAttributes = [
    ...exports.journeyAttributes,
    '_visitedPlaces',
    '_trips',
    '_tripChains'
];
/**
 * A journey is one person's travel on an assigned date.
 *
 * Visited places and trips may be empty. That is still a journey: the person
 * is immobile, or the diary was not opened. `didTrips` keeps the declaration
 * for that day, and `startDate` is the date the survey stored on the journey.
 */
class Journey extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, [
            '_visitedPlaces',
            '_trips',
            '_tripChains',
            'visitedPlaces',
            'trips',
            'tripChains',
            '_personUuid'
        ]), exports.journeyAttributes, exports.journeyAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.visitedPlaces = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._visitedPlaces, VisitedPlace_1.VisitedPlace.unserialize, this._surveyObjectsRegistry);
        this.trips = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._trips, Trip_1.Trip.unserialize, this._surveyObjectsRegistry);
        this.tripChains = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._tripChains, TripChain_1.TripChain.unserialize, this._surveyObjectsRegistry);
        this.personUuid = params._personUuid;
        this._surveyObjectsRegistry.registerJourney(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get startTimePeriod() {
        return this._attributes.startTimePeriod;
    }
    set startTimePeriod(value) {
        this._attributes.startTimePeriod = value;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get endTimePeriod() {
        return this._attributes.endTimePeriod;
    }
    set endTimePeriod(value) {
        this._attributes.endTimePeriod = value;
    }
    get name() {
        return this._attributes.name;
    }
    set name(value) {
        this._attributes.name = value;
    }
    get type() {
        return this._attributes.type;
    }
    set type(value) {
        this._attributes.type = value;
    }
    get noSchoolTripReason() {
        return this._attributes.noSchoolTripReason;
    }
    set noSchoolTripReason(value) {
        this._attributes.noSchoolTripReason = value;
    }
    get noSchoolTripReasonSpecify() {
        return this._attributes.noSchoolTripReasonSpecify;
    }
    set noSchoolTripReasonSpecify(value) {
        this._attributes.noSchoolTripReasonSpecify = value;
    }
    get noWorkTripReason() {
        return this._attributes.noWorkTripReason;
    }
    set noWorkTripReason(value) {
        this._attributes.noWorkTripReason = value;
    }
    get noWorkTripReasonSpecify() {
        return this._attributes.noWorkTripReasonSpecify;
    }
    set noWorkTripReasonSpecify(value) {
        this._attributes.noWorkTripReasonSpecify = value;
    }
    /**
     * Whether the person declared any trips on this journey's date.
     * `yes` and `no` from the questionnaire become a boolean; `dontKnow` stays a non-response.
     * Set from the declaration, not from the trip count: an unfinished interview can
     * have an empty diary while the person did intend to report trips, and a false
     * answer with an empty diary is a valid stay on `startDate`.
     */
    get didTrips() {
        return this._attributes.didTrips;
    }
    set didTrips(value) {
        this._attributes.didTrips = value;
    }
    get _skipTripDiary() {
        return this._attributes._skipTripDiary;
    }
    set _skipTripDiary(value) {
        this._attributes._skipTripDiary = value;
    }
    /**
     * Remote work days for the complete week before the assigned date.
     * If the assigned date is a Monday, this represents Sunday to Saturday
     * of the previous week (not including the assigned Monday).
     * Each day indicates whether the person worked remotely on that day.
     */
    get previousWeekRemoteWorkDays() {
        return this._attributes.previousWeekRemoteWorkDays;
    }
    set previousWeekRemoteWorkDays(value) {
        this._attributes.previousWeekRemoteWorkDays = value;
    }
    /**
     * Travel to work days for the complete week before the assigned date.
     * If the assigned date is a Monday, this represents Sunday to Saturday
     * of the previous week (not including the assigned Monday).
     * Each day indicates whether the person traveled to work on that day.
     */
    get previousWeekTravelToWorkDays() {
        return this._attributes.previousWeekTravelToWorkDays;
    }
    set previousWeekTravelToWorkDays(value) {
        this._attributes.previousWeekTravelToWorkDays = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get visitedPlaces() {
        return this._visitedPlaces;
    }
    set visitedPlaces(value) {
        this._visitedPlaces = value;
    }
    get trips() {
        return this._trips;
    }
    set trips(value) {
        this._trips = value;
    }
    get tripChains() {
        return this._tripChains;
    }
    set tripChains(value) {
        this._tripChains = value;
    }
    get personUuid() {
        return this._personUuid;
    }
    set personUuid(value) {
        this._personUuid = value;
    }
    /**
     * Whether the last questionnaire visited place closed this journey.
     *
     * `true` when the last place answers `nextPlaceCategory === 'stayedThereUntilTheNextDay'`.
     * `false` when there is a last place that did not close the journey.
     * Unset when the journey has no visited place.
     */
    get isJourneyClosed() {
        return this._isJourneyClosed;
    }
    set isJourneyClosed(value) {
        this._isJourneyClosed = value;
    }
    /**
     * Whether more than one questionnaire visited place closed this journey.
     *
     * `true` when more than one place answers `nextPlaceCategory === 'stayedThereUntilTheNextDay'`.
     * `false` when at most one place closed the journey.
     */
    get isJourneyClosedMoreThanOnce() {
        return this._isJourneyClosedMoreThanOnce;
    }
    set isJourneyClosedMoreThanOnce(value) {
        this._isJourneyClosedMoreThanOnce = value;
    }
    get person() {
        if (!this._personUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getPerson(this._personUuid);
    }
    get household() {
        var _a;
        return (_a = this.person) === null || _a === void 0 ? void 0 : _a.household;
    }
    /**
     * Add a visited place to this journey
     */
    addVisitedPlace(visitedPlace) {
        if (!this._visitedPlaces) {
            this._visitedPlaces = [];
        }
        this._visitedPlaces.push(visitedPlace);
    }
    /**
     * Insert a visited place at a specific index
     */
    insertVisitedPlace(visitedPlace, index) {
        if (!this._visitedPlaces) {
            this._visitedPlaces = [];
        }
        this._visitedPlaces.splice(index, 0, visitedPlace);
    }
    /**
     * Insert a visited place after another visited place with the specified UUID
     */
    insertVisitedPlaceAfterUuid(visitedPlace, afterUuid) {
        if (!this._visitedPlaces) {
            this._visitedPlaces = [];
        }
        // If array is empty, add the visited place
        if (this._visitedPlaces.length === 0) {
            this._visitedPlaces.push(visitedPlace);
            return true;
        }
        const index = this._visitedPlaces.findIndex((vp) => vp._uuid === afterUuid);
        if (index >= 0) {
            this._visitedPlaces.splice(index + 1, 0, visitedPlace);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Insert a visited place before another visited place with the specified UUID
     */
    insertVisitedPlaceBeforeUuid(visitedPlace, beforeUuid) {
        if (!this._visitedPlaces) {
            this._visitedPlaces = [];
        }
        // If array is empty, add the visited place
        if (this._visitedPlaces.length === 0) {
            this._visitedPlaces.push(visitedPlace);
            return true;
        }
        const index = this._visitedPlaces.findIndex((vp) => vp._uuid === beforeUuid);
        if (index >= 0) {
            this._visitedPlaces.splice(index, 0, visitedPlace);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Remove a visited place from this journey by UUID
     */
    removeVisitedPlace(visitedPlaceUuid) {
        if (!this._visitedPlaces) {
            return false;
        }
        const index = this._visitedPlaces.findIndex((vp) => vp._uuid === visitedPlaceUuid);
        if (index >= 0) {
            this._visitedPlaces.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Get a visited place by UUID
     */
    getVisitedPlaceByUuid(visitedPlaceUuid) {
        if (!this._visitedPlaces) {
            return undefined;
        }
        return this._visitedPlaces.find((vp) => vp._uuid === visitedPlaceUuid);
    }
    /**
     * Add a trip to this journey
     */
    addTrip(trip) {
        if (!this._trips) {
            this._trips = [];
        }
        this._trips.push(trip);
    }
    /**
     * Insert a trip at a specific index
     */
    insertTrip(trip, index) {
        if (!this._trips) {
            this._trips = [];
        }
        this._trips.splice(index, 0, trip);
    }
    /**
     * Insert a trip after another trip with the specified UUID
     */
    insertTripAfterUuid(trip, afterUuid) {
        if (!this._trips) {
            this._trips = [];
        }
        // If array is empty, add the trip
        if (this._trips.length === 0) {
            this._trips.push(trip);
            return true;
        }
        const index = this._trips.findIndex((t) => t._uuid === afterUuid);
        if (index >= 0) {
            this._trips.splice(index + 1, 0, trip);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Insert a trip before another trip with the specified UUID
     */
    insertTripBeforeUuid(trip, beforeUuid) {
        if (!this._trips) {
            this._trips = [];
        }
        // If array is empty, add the trip
        if (this._trips.length === 0) {
            this._trips.push(trip);
            return true;
        }
        const index = this._trips.findIndex((t) => t._uuid === beforeUuid);
        if (index >= 0) {
            this._trips.splice(index, 0, trip);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Remove a trip from this journey by UUID
     */
    removeTrip(tripUuid) {
        if (!this._trips) {
            return false;
        }
        const index = this._trips.findIndex((trip) => trip._uuid === tripUuid);
        if (index >= 0) {
            this._trips.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Get a trip by UUID
     */
    getTripByUuid(tripUuid) {
        if (!this._trips) {
            return undefined;
        }
        return this._trips.find((trip) => trip._uuid === tripUuid);
    }
    /**
     * Creates a Journey object from sanitized parameters.
     * `didTrips` is wrapped the same way as in `create`, so a value stored as
     * `yes` / `no` / `dontKnow` becomes an `AnswerStatus<boolean>`.
     * @param {ExtendedJourneyAttributes | SerializedExtendedJourneyAttributes} params - Sanitized journey parameters
     * @returns {Journey} New Journey instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        const normalizedParams = Journey.wrapAnswerStatuses(flattenedParams);
        return new Journey(normalizedParams, surveyObjectsRegistry);
    }
    /**
     * Wrap `didTrips` as the questionnaire stores it (`yes`/`no`/`dontKnow` or
     * a boolean) in its status. A blank one is omitted so that it does not
     * read as an answer of the wrong shape. `_skipTripDiary` means the question
     * was not asked, so `didTrips` is `not_applicable` whatever was stored.
     *
     * @param {Object} dirtyParams The parameters as read from the response
     * @returns {Object} A copy of the parameters, with `didTrips` wrapped.
     * Parameters that are not an object are returned as they are, for
     * `validateParams` to report.
     */
    static wrapAnswerStatuses(dirtyParams) {
        if (typeof dirtyParams !== 'object' || dirtyParams === null) {
            return dirtyParams;
        }
        const didTrips = dirtyParams._skipTripDiary === true
            ? { status: 'not_applicable' }
            : (0, AnswerStatus_1.toBooleanAnswerStatus)(dirtyParams.didTrips);
        return didTrips === undefined ? (0, omit_1.default)(dirtyParams, ['didTrips']) : Object.assign(Object.assign({}, dirtyParams), { didTrips });
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const params = Journey.wrapAnswerStatuses(dirtyParams);
        const errors = Journey.validateParams(params);
        const journey = errors.length === 0 ? new Journey(params, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(journey);
    }
    static validateParams(dirtyParams, displayName = 'Journey') {
        const errors = [];
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams, displayName));
        errors.push(...StartEndable_1.StartEndable.validateParams(dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('_sequence', dirtyParams._sequence, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('name', dirtyParams.name, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('type', dirtyParams.type, displayName));
        // Validate new attributes
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('noSchoolTripReason', dirtyParams.noSchoolTripReason, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('noSchoolTripReasonSpecify', dirtyParams.noSchoolTripReasonSpecify, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('noWorkTripReason', dirtyParams.noWorkTripReason, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('noWorkTripReasonSpecify', dirtyParams.noWorkTripReasonSpecify, displayName));
        errors.push(...(0, AnswerStatus_1.validateAnswerStatus)('didTrips', dirtyParams.didTrips, displayName, ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_skipTripDiary', dirtyParams._skipTripDiary, displayName));
        // Validate work schedule attributes
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('previousWeekRemoteWorkDays', dirtyParams.previousWeekRemoteWorkDays, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('previousWeekTravelToWorkDays', dirtyParams.previousWeekTravelToWorkDays, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        const visitedPlacesAttributes = dirtyParams._visitedPlaces !== undefined
            ? dirtyParams._visitedPlaces
            : [];
        for (let i = 0, countI = visitedPlacesAttributes.length; i < countI; i++) {
            const visitedPlaceAttributes = visitedPlacesAttributes[i];
            errors.push(...VisitedPlace_1.VisitedPlace.validateParams(visitedPlaceAttributes, 'VisitedPlace'));
        }
        const tripsAttributes = dirtyParams._trips !== undefined ? dirtyParams._trips : [];
        for (let i = 0, countI = tripsAttributes.length; i < countI; i++) {
            const tripAttributes = tripsAttributes[i];
            errors.push(...Trip_1.Trip.validateParams(tripAttributes, 'Trip'));
        }
        const tripChainsAttributes = dirtyParams._tripChains !== undefined ? dirtyParams._tripChains : [];
        for (let i = 0, countI = tripChainsAttributes.length; i < countI; i++) {
            const tripChainAttributes = tripChainsAttributes[i];
            errors.push(...TripChain_1.TripChain.validateParams(tripChainAttributes, 'TripChain'));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_personUuid', dirtyParams._personUuid, displayName));
        return errors;
    }
}
exports.Journey = Journey;
Journey._confidentialAttributes = ['preData'];
//# sourceMappingURL=Journey.js.map